/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type IntlService from 'embercom/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';

export default class GettingStartedRoute extends Route {
  @service declare appService: AppService;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  private get isAiOnboardingEnabled(): boolean {
    // `buildRouteInfoMetadata()` is called by the router very early (for Sentry
    // team-tagging via responsibleTeamFromTransition) BEFORE beforeModel/model
    // run, when `appService.app` may still be undefined. Default to `false` in
    // that window — once the app loads, the actual route hooks evaluate against
    // the populated service. Mirrors inbox's `session.isWorkspaceLoaded` guard.
    return this.appService.app?.canUseFeature('team-self-serve-ai-onboarding') ?? false;
  }

  // The React island bridge gates rendering on `metadata.react` (see
  // `embercom/lib/react-route#isReactRoute`). Without this metadata the
  // island container stays hidden and `route:boot` is never dispatched.
  buildRouteInfoMetadata() {
    return {
      responsibleTeam: 'team-self-serve',
      react: this.isAiOnboardingEnabled,
    };
  }

  async beforeModel() {
    // When the AI onboarding chat is enabled, skip checklist-specific setup —
    // the React island owns the experience and the legacy services aren't needed.
    if (this.isAiOnboardingEnabled) {
      return;
    }
    await this.finStandaloneService.initialize();
  }

  async model(_: $TSFixMe, transition: Transition) {
    // Skip the checklist fetch + completion-redirect when the AI onboarding flag is on.
    if (this.isAiOnboardingEnabled) {
      return null;
    }
    let steps = await this.greatGuidanceService.fetchSteps(this.appService.app.id);
    if (transition.from?.name === 'apps_loading' && this.appService.app.isStandaloneApp) {
      let hasIncompleteSteps = steps.some((step) => !step.completed);

      if (!hasIncompleteSteps) {
        return this.transitionTo('apps.app.standalone.reports');
      }
    }
    return steps;
  }

  get titleToken() {
    return this.intl.t('standalone.navbar.getting-started');
  }
}
